import type { LeadCard, LeadCardCall, LeadCardCampaign } from "@/lib/types";
import { formatDuration } from "@/lib/utils";

/**
 * The lead's card on the rep's phone (ADR-024): the words for what `GET /leads/{id}` says. Pure, for
 * `test/lead-card.test.ts`.
 */

/** The time where the lead is, as a rep would say it: "2:14 PM". Null without a timezone this
 *  browser knows. */
export function localTime(timezone: string | null, now: number): string | null {
  if (!timezone) return null;
  try {
    const text = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    }).format(now);
    // Newer ICU puts a narrow no-break space before "PM"; the rest of the phone uses a plain one.
    return text.replaceAll(" ", " ");
  } catch {
    return null;
  }
}

const LANGUAGES: Record<string, string> = { en: "English", es: "Spanish" };

/** "es" -> "Spanish". A code the console has no word for is shown as the code. */
export function languageName(code: string | null): string | null {
  if (!code) return null;
  return LANGUAGES[code.toLowerCase()] ?? code.toUpperCase();
}

/** A slug in the rep's words: "contract_review" -> "Contract review". */
export function humanize(slug: string | null): string | null {
  const text = slug?.replaceAll("_", " ").trim();
  return text ? text[0]!.toUpperCase() + text.slice(1) : null;
}

/** The lead on the list of the campaign the call came through, else the list they were last called
 *  from. */
export function listFor(
  card: Pick<LeadCard, "campaigns">,
  campaignId: string | null,
): LeadCardCampaign | null {
  const here = card.campaigns.find((c) => c.campaign_id === campaignId);
  if (here) return here;
  const latest = [...card.campaigns].sort((a, b) =>
    (b.last_attempt_at ?? "").localeCompare(a.last_attempt_at ?? ""),
  );
  return latest[0] ?? null;
}

/** How a stay in a line ended without a conversation, by the event-tracker's outcome. */
const UNANSWERED: Record<string, string> = {
  abandoned: "Hung up while waiting",
  drop: "Hung up as it connected",
  timeout: "Nobody was free",
};

/**
 * One of the lead's calls, in a line: "Talked 3:32 with Marcus Lee · Callback", "Hung up while
 * waiting", "No answer · power dial". `label` turns an outcome's slug into the campaign's word for
 * it; a slug it does not know is shown in plain words.
 */
export function callText(call: LeadCardCall, label: (slug: string) => string | undefined): string {
  let text: string;
  if (call.via === "queue" && call.talk_seconds) {
    text = `Talked ${formatDuration(Math.round(call.talk_seconds))}`;
    if (call.rep_name) text += ` with ${call.rep_name}`;
  } else if (call.via === "queue") {
    // The event-tracker writes a stay's outcome in capitals: CONNECTED, ABANDONED, REHOMED.
    const outcome = call.outcome?.toLowerCase() ?? "";
    text = UNANSWERED[outcome] ?? humanize(outcome) ?? "Did not connect";
  } else {
    text = humanize(call.outcome) ?? "Dialed";
    if (call.kind) text += ` · ${call.kind} dial`;
  }
  if (call.disposition) text += ` · ${label(call.disposition) ?? humanize(call.disposition)}`;
  return text;
}
